import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Button,
  Input,
  FormGroup,
  Label,
  Card,
  CardBody,
  Badge,
  Spinner,
  Row,
  Col,
  Collapse
} from 'reactstrap';
import { Clock, Video, RefreshCw, Plus, ChevronDown, ChevronsUp } from 'lucide-react';
import axios from 'axios';
import { fetchZakToken, fetchZoomUserData, createAndfetchMeetingDetails, fetchZoomSignature, disableWaitingRoom, endZoomMeeting } from 'helpers/zoom';
import { refreshToken } from 'helpers/zoomAuth';
import MeasurementRow from './MeasurementRow';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const STAGE = process.env.REACT_APP_ENV_RESOURCE;
const POLLING_INTERVAL = 20000; // 20 seconds

const meetingOptions = [
  { value: 1, label: '1 Meeting' },
  { value: 2, label: '2 Meetings' },
  { value: 5, label: '5 Meetings' },
  { value: 10, label: '10 Meetings' },
  { value: 20, label: '20 Meetings' },
];

const Roles = {
  START: 1,
  JOIN: 0
}

const TeleconferenceModal = ({ isOpen, toggle, subjectId, subjectEmail }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMeetingCount, setSelectedMeetingCount] = useState(5);
  const [showWarning, setShowWarning] = useState(true);
  const [creatingMeetings, setCreatingMeetings] = useState(false);
  const [joiningMeeting, setJoiningMeeting] = useState(false);
  const [pollingError, setPollingError] = useState(null);
  const [calibrationExists, setCalibrationExists] = useState(false);
  const [isMeasurementsOpen, setIsMeasurementsOpen] = useState(false);

  const [showOlderCompleted, setShowOlderCompleted] = useState(false);

  const completedMeetings = meetings.filter(
    (m) => !m.meetingActive && m.meetingJoinedTime > 0
  );
  const nonCompletedMeetings = meetings.filter(
    (m) => !completedMeetings.includes(m)
  );

  let lastCompleted = null;
  if (completedMeetings.length > 0) {
    lastCompleted = completedMeetings[completedMeetings.length - 1];
  }

  const olderCompleted = completedMeetings.slice(0, -1);

  const toggleMeasurements = () => {
    setIsMeasurementsOpen(!isMeasurementsOpen);
  };

  useEffect(() => {
    if (!isOpen) {
      setShowWarning(true);
    }
  }, [isOpen]);

  useEffect(() => {
    let pollTimer;

    const setupPolling = async () => {
      if (isOpen) {
        // Initial fetch
        await fetchMeetings(true);

        // Setup polling
        pollTimer = setInterval(async () => {
          await fetchMeetings(false);
        }, POLLING_INTERVAL);
      }
    };

    setupPolling();

    // Cleanup function
    return () => {
      if (pollTimer) {
        clearInterval(pollTimer);
      }
    };
  }, [isOpen, subjectId]);

  // Function to fetch meetings
  const fetchMeetings = async (showLoadingState = false) => {
    try {
      // Only show loading state on initial load
      if (showLoadingState && meetings.length === 0) {
        setLoading(true);
      }

      const response = await axios.post(`${BASE_URL}web/teleconference/meetings`, {
        subjectId
      });

      // Compare new data with existing data before updating state
      const newMeetings = response.data.meetings;
      const newCalibrationExists = response.data.calibrationExist;

      // Only update meetings if there's a change
      if (JSON.stringify(newMeetings) !== JSON.stringify(meetings)) {
        setMeetings(newMeetings);
      }

      // Only update calibration status if it changed
      if (newCalibrationExists !== calibrationExists) {
        setCalibrationExists(newCalibrationExists);
      }

      if (meetings.length > 0) {
        setShowWarning(false);
      }
      setPollingError(null);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setPollingError('Failed to refresh meeting data');
    } finally {
      if (showLoadingState && meetings.length === 0) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isOpen) {
        console.log('Document is visible, fetching meetings...');
        await fetchMeetings(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen]);

  const handleCreateMeetings = async () => {
    try {
      setCreatingMeetings(true);

      const response = await axios.post(`${BASE_URL}web/teleconference/createMeetings`, {
        firstMeetingLink: null,
        numberOfMeetingsRequired: selectedMeetingCount,
        subjectId
      });

      if (response.data.success) {
        await fetchMeetings();
      }
    } catch (error) {
      console.error('Error creating meetings:', error);
    } finally {
      setCreatingMeetings(false);
    }
  };

  const handleJoinMeeting = async (meetingId, meetingLink, previousMeetingId, previousMeetingZoomNumber) => {
    setJoiningMeeting(true);
    const zoomAccessToken = await refreshToken(() => window.location.href = `${process.env.REACT_APP_CAREX_ZOOM_SERVICE_ENDPOINT}/oauth`);

    // ensure the previous meeting has ended before joining the next meeting
    if (previousMeetingId) {
      try {
        await endZoomMeeting(previousMeetingId, previousMeetingZoomNumber);
      } catch (error) {
        console.error('Error ending previous meeting:', error);
      }
    }

    if (meetingLink) {
      if (!meetingLink.includes('meetingId')) {
        window.open(meetingLink + `&meetingId=${meetingId}`, '_blank');
      } else {
        window.open(meetingLink, '_blank');
      }
      setJoiningMeeting(false);
    } else {
      try {
        const [zakToken, userData, meetingDetails] = await Promise.all([fetchZakToken(), fetchZoomUserData(), createAndfetchMeetingDetails()]);

        if (!zakToken || !userData || !meetingDetails) {
          console.error(`Failed to fetch required data: zakToken: ${zakToken}, userData: ${userData}, meetingDetails: ${meetingDetails}`);
          alert('Failed to fetch required Zoom data.');
          return;
        }

        const signature = await fetchZoomSignature(meetingDetails.id, Roles.START);

        const newMeetingLink = `${process.env.REACT_APP_ZOOM_WEB_APP_URL}/meeting?signature=${signature}&meetingNumber=${meetingDetails.id}&meetingPassword=${meetingDetails.password}&displayName=${userData.displayName}&email=${userData.email}&zakToken=${zakToken}&meetingId=${meetingId}&participantId=${userData.id}&zoomAccessToken=${zoomAccessToken}`;

        await axios.post(`${BASE_URL}web/teleconference/createMeetingLink`, {
          meetingLink: newMeetingLink,
          meetingId,
          subjectId
        });

        await fetchMeetings();
        window.open(newMeetingLink, '_blank');
      } catch (error) {
        console.error('Error creating meeting link:', error);
      }

      setJoiningMeeting(false);
    }
  };

  const formatTime = (epochSeconds) => {
    if (!epochSeconds || epochSeconds === 0) return null;
    return new Date(epochSeconds * 1000).toLocaleString();
  };

  const getActiveMeetingIndex = () => {
    let activeMeetingIndex = meetings.findIndex(meeting => meeting.meetingActive);
    if (activeMeetingIndex === -1) {
      if (meetings.length !== meetings.findLastIndex(meeting => meeting.meetingJoinedTime > 0)) {
        activeMeetingIndex = meetings.findLastIndex(meeting => meeting.meetingJoinedTime > 0);
      }
    }
    return activeMeetingIndex;
  };
  const nonJoinedActiveMeetings = () => {
    return meetings.filter(meeting => meeting.meetingActive && meeting.meetingJoinedTime === 0).length > 0;
  }
  const CalibrationWarning = () => (
    <Alert color="danger" className="mb-4">
      <div className="d-flex align-items-center mb-3">
        <div className="alert-icon me-3">
          <i className="fas fa-exclamation-triangle fa-2x"></i>
        </div>
        <h5 className="alert-heading mb-0">Calibration Required</h5>
      </div>
      <div className="calibration-message">
        <p className="mb-3">Before you can create or join meetings, the patient needs to complete the calibration process in the Vitouch application.</p>
        <ol className="mb-3">
          <li>Open the Vitouch application</li>
          <li>Log in with the following credentials:
            <div className="bg-white p-2 rounded mt-2 mb-2">
              <div><strong>Email:</strong> {subjectEmail}</div>
              <div><strong>Password:</strong> 123456</div>
            </div>
          </li>
          <li>Complete the calibration process</li>
          <li>Once calibration is complete, this warning will automatically disappear</li>
        </ol>
      </div>
    </Alert>
  );

  const renderMeetingCard = (meeting, index) => {
    const isActive = meeting.meetingActive || (index === 0 && getActiveMeetingIndex() === -1);
    const isCompleted = !isActive && meeting.meetingJoinedTime > 0;
    const isNext = (index === getActiveMeetingIndex() + 1 && index !== 0 && !nonJoinedActiveMeetings());
    const hasNoMeasurements = !meeting.measurements || meeting.measurements.length === 0;
    const previousMeeting = index > 0 ? meetings[index - 1] : null;
    const isPreviousMeetingJoinable = previousMeeting && previousMeeting.meetingJoinedTime > 0 && previousMeeting.meetingActive;
    // Extract the meeting 
    let previousMeetingZoomNumber, previousMeetingId;
    if (isPreviousMeetingJoinable && previousMeeting.meetingLink) {
      const match = previousMeeting.meetingLink.match(/meetingNumber=(\d+)/);
      if (match) {
        previousMeetingZoomNumber = match[1];
        console.log(`Previous meeting Zoom ID: ${previousMeetingZoomNumber}`);
      } else {
        console.error('Failed to extract previous meeting Zoom ID');
      }
      previousMeetingId = previousMeeting.meetingId;
    }

    console.log(`Meeting ${meeting.meetingId} is active: ${isActive}, previousMeetingZoomNumber: ${previousMeetingZoomNumber}, isPreviousMeetingJoinable: ${isPreviousMeetingJoinable}, previousMeetingId: ${previousMeetingId}`);

    return (
      <Card
        key={meeting.meetingId}
        className="meeting-card mb-2"
        style={{
          borderLeft: isActive ? '4px solid #3b82f6' :
            isNext ? '4px solid #10b981' :
              isCompleted ? '4px solid #6b7280' : '4px solid transparent'
        }}
      >
        <CardBody style={{ padding: '16px', display: 'flex', justifyContent: 'space-between' }}>
          {/* Top row: Meeting name/badges on the left, doctor/patient times on the right */}
          {/* <div className="d-flex justify-content-between align-items-center"> */}
          {/* Left side: Meeting name + status badges */}
          <div className="d-flex align-items-center">
            <h5 className="mb-0" style={{ color: isCompleted ? '#6b7280' : '#111827', marginRight: '4px' }}>
              {meeting.meetingName}
            </h5>
            {isActive && (
              <Badge color="primary" className="mr-2" pill style={{ marginRight: '4px' }}>
                Active
              </Badge>
            )}
            {isNext && (
              <Badge color="success" className="mr-2" pill style={{ marginRight: '4px' }}>
                Next
              </Badge>
            )}
            {isCompleted && (
              <Badge color="secondary" pill style={{ marginRight: '4px' }}>
                Completed
              </Badge>
            )}
          </div>

          {(meeting.meetingJoinedTime > 0 || meeting.patientJoinTime > 0) && (
            <div>
              {meeting.meetingJoinedTime > 0 && (
                <span className="mr-3" style={{ marginRight: '6px' }}>
                  <Clock size={16} style={{ marginRight: '2px' }} />
                  <strong>Doctor:</strong> {formatTime(meeting.meetingJoinedTime)}
                  {meeting.meetingExitedTime > 0 && (
                    <> - {formatTime(meeting.meetingExitedTime)}</>
                  )}
                </span>
              )}
              {meeting.patientJoinTime > 0 && (
                <span>
                  <Clock size={16} style={{ marginRight: '4px' }} />
                  <strong>Patient:</strong> {formatTime(meeting.patientJoinTime)}
                  {meeting.patientExitTime > 0 && (
                    <> - {formatTime(meeting.patientExitTime)}</>
                  )}
                </span>
              )}
            </div>
          )}
          {/* </div> */}

          {/* Second row: Join button (if active/next) */}
          {(isActive || isNext) && (
            <div>
              <Button
                color={isActive ? 'primary' : 'success'}
                className="join-button"
                onClick={() =>
                  handleJoinMeeting(
                    meeting.meetingId,
                    meeting.meetingLink,
                    previousMeetingId,
                    previousMeetingZoomNumber
                  )
                }
                disabled={joiningMeeting}
              >
                <Video size={18} className="mr-1" />
                <span>{meeting.meetingJoinedTime > 0 ? 'Rejoin Meeting' : 'Join Meeting'}</span>
              </Button>
            </div>
          )}

        </CardBody>
        {/* Render measurements if they exist */}
        {meeting.measurements.map((measurement, idx) => (
          <MeasurementRow
            key={measurement.measurementId}
            measurement={measurement}
            index={idx}
          />
        ))}
        {/* {!hasNoMeasurements && (
          <>
            <Button
              color="link"
              className="p-0"
              onClick={toggleMeasurements}
              style={{ textDecoration: 'none' }}
            >
              {isMeasurementsOpen ? 'Hide Measurements' : 'Show Measurements'}
            </Button>

            <Collapse isOpen={isMeasurementsOpen}>

            </Collapse>
          </>
        )} */}
      </Card>
    );
  };


  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle} className="border-0">
        <h4 className="mb-0">Teleconference Meetings</h4>
      </ModalHeader>
      <ModalBody>
        {(!calibrationExists && STAGE !== "dca-develop") ? (
          <CalibrationWarning />
        ) : (
          <>
            {meetings.length > 0 && (
              <div className="bg-light p-2 rounded mb-3 border">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex gap-3">
                    <span><strong>Email:</strong> {subjectEmail}</span>
                    <span><strong>Password:</strong> 123456</span>
                  </div>
                  <small className="text-muted">Vitouch Login Credentials</small>
                </div>
              </div>
            )
            }
            {pollingError && (
              <Alert color="warning" className="mb-3" toggle={() => setPollingError(null)}>
                {pollingError}
              </Alert>
            )}
            {loading ? (
              <div className="d-flex justify-content-center align-items-center p-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                <div className="meeting-controls">
                  <h5 className="mb-4">Create New Meetings</h5>
                  <Row className="align-items-center">
                    <Col md={8}>
                      <FormGroup className="mb-0">
                        <Label for="meetingCount" className="text-muted mb-2">
                          Number of meetings to create
                        </Label>
                        <Input
                          type="select"
                          value={selectedMeetingCount}
                          onChange={(e) => setSelectedMeetingCount(Number(e.target.value))}
                          className="form-control-lg"
                        >
                          {meetingOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <Button
                        color="success"
                        onClick={handleCreateMeetings}
                        disabled={creatingMeetings}
                        className="create-button w-100"
                      >
                        {creatingMeetings ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <Plus size={20} />
                            <span>Create</span>
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </div>

                <div className="meetings-list">
                  {completedMeetings.length === 1 && renderMeetingCard(lastCompleted, meetings.indexOf(lastCompleted))}

                  {completedMeetings.length > 1 && (
                    <>
                      <Button
                        className="join-button d-flex align-items-center mx-auto mb-2"
                        onClick={() => setShowOlderCompleted(!showOlderCompleted)}
                      >
                        {showOlderCompleted ? <ChevronsUp size={16} /> : <ChevronDown size={16} />}
                        <span className="ml-2">
                          {showOlderCompleted ? 'Hide Previous Meetings' : 'Show Previous Meetings'}
                        </span>
                      </Button>

                      <Collapse isOpen={showOlderCompleted}>
                        {olderCompleted.map((meeting) => {
                          const originalIndex = meetings.indexOf(meeting);
                          return renderMeetingCard(meeting, originalIndex);
                        })}
                      </Collapse>
                      {renderMeetingCard(lastCompleted, meetings.indexOf(lastCompleted))}
                    </>
                  )}
                  {nonCompletedMeetings.map((meeting, idx) => {
                    const originalIndex = meetings.indexOf(meeting);
                    return renderMeetingCard(meeting, originalIndex);
                  })}

                  {/* 7. If more than 1 completed meeting: show the LAST one, then a collapsible for the older ones */}
                </div>

                {meetings.length > 0 && (
                  <Button
                    className="join-button d-flex align-items-center mx-auto mt-4"
                    onClick={fetchMeetings}
                  >
                    <RefreshCw size={16} />
                    <span className="ml-2">Refresh Meetings</span>
                  </Button>
                )}

              </>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default TeleconferenceModal;